// ============================================================================
// PairsForm - Sidebar Navigation
// ============================================================================

import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
  Typography,
  Tooltip,
  Chip,
  Badge,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
} from "lucide-react";
import { useNavigation } from "../../hooks/useNavigation";
import { useAuth } from "../../contexts/AuthContext";
import { NIVEAUX } from "@cecodev/shared";
import { ROLE_LABELS } from "../../lib/navigation/types";
import type { MenuSection, MenuItem } from "../../lib/navigation/types";
import { collecterHrefsMenu, estHrefActif } from "../../lib/navigation/menu-actif";
import { OrgSwitcher } from "../OrgSwitcher";
import { estCheminPersonnel } from "../../lib/urls";

const SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;

// Espace personnel : aucun sélecteur d'organisation (MENU_CIBLE §2 — Phase 1
// refonte navigation, 2026-08-05). Énumération explicite plutôt qu'un préfixe
// « /mes- » : /mes-actions est le cockpit de PILOTAGE (org-scopé) et doit
// GARDER le sélecteur.
// Lot 9.1 (2026-10-18) : le niveau personnel a UN préfixe (/mon-espace) ; la
// liste des chemins où le sélecteur d'org se masque vit dans lib/urls.ts.


interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  variant?: "permanent" | "temporary";
}

export function Sidebar({ open = true, onClose, variant = "permanent" }: SidebarProps) {
  const location = useLocation();
  const { menuSections, user } = useNavigation();
  const { logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Surbrillance : UN SEUL item actif — le préfixe le plus long du pathname
  // parmi TOUS les hrefs du menu (recette FX 2026-08-29 : l'ancienne garde ne
  // comparait qu'aux frères de section — « Mon profil » /espace-perso restait
  // allumé sur /espace-perso/e-learning, item d'une autre section).
  const tousLesHrefs = useMemo(() => collecterHrefsMenu(menuSections), [menuSections]);

  // Vérifier si un chemin est actif (utilisé pour sections à lien direct)
  const isActivePath = (hrefBrut: string): boolean =>
    estHrefActif(hrefBrut, location.pathname, tousLesHrefs, location.search);

  // Vérifier si un item de sous-menu est actif — même règle unique.
  const isItemActive = (hrefBrut: string, _siblings?: MenuItem[]): boolean =>
    estHrefActif(hrefBrut, location.pathname, tousLesHrefs, location.search);

  // Vérifier si une section a un item actif
  const sectionHasActiveItem = (section: MenuSection): boolean => {
    if (section.href) {
      return isActivePath(section.href);
    }
    if (section.items) {
      return section.items.some(item => !item.disabled && isActivePath(item.href));
    }
    return false;
  };

  // Auto-ouvrir les sections contenant un item actif à chaque changement de route
  useEffect(() => {
    if (collapsed) return;
    setOpenSections(prev => {
      const next = { ...prev };
      menuSections.forEach(section => {
        if (section.items && sectionHasActiveItem(section)) {
          next[section.id] = true;
        }
      });
      return next;
    });
  }, [location.pathname, collapsed]);

  // Toggle section. Barre REPLIÉE : le sous-menu n'est pas rendu, inverser
  // l'état serait invisible (retour bêta 15/09 : icônes « mortes ») — on
  // déplie la barre et on OUVRE la section cliquée.
  const toggleSection = (sectionId: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenSections(prev => ({ ...prev, [sectionId]: true }));
      return;
    }
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Toggle collapse
  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
    if (!collapsed) {
      setOpenSections({});
    } else {
      // Le useEffect sur location.pathname se chargera de ré-ouvrir les sections actives
    }
  };

  const drawerWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {!collapsed && (
          /* Le logo remplace le texte « PairsForm / Intelligence collective »
             (FX 2026-08-18) : il porte déjà les deux, en mieux. Présent sur
             toutes les pages puisque la barre latérale les enveloppe. */
          /* Cliquable (retour bêta 15/09) : retour à l'accueil — « / » route
             un connecté vers son tableau de bord (LandingPage → /dashboard). */
          <Link to="/" style={{ maxWidth: "80%", display: "block" }}>
            <Box
              component="img"
              src="/logo-pairsform-cecodev-v2.png"
              alt="Pairsform — retour à l'accueil"
              sx={{ height: 40, width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }}
            />
          </Link>
        )}
        {variant === "permanent" && (
          <IconButton size="small" onClick={handleToggleCollapse}>
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </IconButton>
        )}
      </Box>

      {/* User Info */}
      {!collapsed && user && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user.prenom} {user.nom}
            </Typography>
            {/* Badge du parcours certifié (FX 2026-08-18) : porté par la
                session, donc aucun appel réseau ici. L'image seule — le libellé
                complet vit dans le profil. */}
            {user.niveauCertification && NIVEAUX[user.niveauCertification]?.badge && (
              <Tooltip title={`${NIVEAUX[user.niveauCertification].court} — ${NIVEAUX[user.niveauCertification].description}`}>
                <Box
                  component="img"
                  src={NIVEAUX[user.niveauCertification].badge!}
                  alt={NIVEAUX[user.niveauCertification].court}
                  sx={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {user.organisationNom || "Organisation"}
          </Typography>
          <Chip
            label={ROLE_LABELS[user.roles[0]]}
            size="small"
            color="primary"
            sx={{ mt: 1, fontSize: "0.7rem", height: 20 }}
          />
        </Box>
      )}

      {/* Organisation Switcher — masqué dans l'espace personnel (Phase 1).
          Le filet « Mes espaces » ouvert le 2026-08-19 pour les multi-membership
          est REFERMÉ le jour même (FX) : la bascule implicite rend la porte
          inutile, et une porte visible trahit ce qu'on veut cacher — « cela
          doit être invisible pour l'utilisateur ». */}
      {!collapsed && !estCheminPersonnel(location.pathname) && <OrgSwitcher />}

      {/* Navigation — data-testid : ancres stables du balayage e2e du menu
          (menu-sweep.spec.ts), ne pas retirer sans adapter la spec. */}
      <Box data-testid="menu-nav" sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List>
          {menuSections.map((section, index) => (
            <Box key={section.id}>
              {section.separator && index > 0 && (
                <Divider sx={{ my: 1 }} />
              )}

              {/* Section avec lien direct */}
              {section.href && !section.items && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to={section.href}
                    selected={isActivePath(section.href)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      "&.Mui-selected": {
                        bgcolor: "rgba(225, 81, 44, 0.08)",
                        borderLeft: "3px solid #E1512C",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: "rgba(225, 81, 44, 0.12)",
                        },
                        "& .MuiListItemIcon-root": {
                          color: "#E1512C",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40 }}>
                      <section.icon size={20} />
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary={section.title} />}
                    {!collapsed && section.badge != null && section.badge > 0 && (
                      <Chip
                        label={section.badge > 99 ? "99+" : section.badge}
                        size="small"
                        color="error"
                        sx={{ height: 18, fontSize: "0.65rem", minWidth: 18 }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              )}

              {/* Section avec sous-menu */}
              {section.items && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      data-testid="menu-section-toggle"
                      onClick={() => toggleSection(section.id)}
                      sx={{
                        mx: 1,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40 }}>
                        <section.icon size={20} />
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText primary={section.title} />
                          {openSections[section.id] ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                  {!collapsed && (
                    <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {section.items.map((item) => (
                          <ListItem key={item.id} disablePadding>
                            {item.disabled ? (
                              <ListItemText
                                primary={item.label}
                                sx={{
                                  pl: 4,
                                  py: 0.5,
                                  color: "text.disabled",
                                  fontSize: "0.875rem",
                                }}
                              />
                            ) : (
                              <ListItemButton
                                component={Link}
                                to={item.href}
                                selected={isItemActive(item.href, section.items)}
                                sx={{
                                  pl: 4,
                                  py: 0.5,
                                  mx: 1,
                                  borderRadius: 1,
                                  "&.Mui-selected": {
                                    bgcolor: "rgba(225, 81, 44, 0.08)",
                                    borderLeft: "3px solid #E1512C",
                                    fontWeight: 600,
                                    "&:hover": {
                                      bgcolor: "rgba(225, 81, 44, 0.12)",
                                    },
                                  },
                                }}
                              >
                                {item.icon && (
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <item.icon size={16} />
                                  </ListItemIcon>
                                )}
                                <ListItemText
                                  primary={item.label}
                                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                                />
                                {item.badge != null && item.badge > 0 && (
                                  <Chip
                                    label={item.badge > 99 ? "99+" : item.badge}
                                    size="small"
                                    color="error"
                                    sx={{ height: 18, fontSize: "0.65rem", minWidth: 18 }}
                                  />
                                )}
                              </ListItemButton>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </>
              )}
            </Box>
          ))}
        </List>
      </Box>

      {/* Footer - Déconnexion */}
      <Box sx={{ borderTop: 1, borderColor: "divider", p: 1 }}>
        <ListItemButton
          onClick={logout}
          sx={{
            mx: 1,
            borderRadius: 1,
            color: "error.main",
          }}
        >
          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: "error.main" }}>
            <LogOut size={20} />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Déconnexion" />}
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          transition: "width 0.2s",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

